<template>
  <div class="in-dm-card">
    <!-- カード画像 -->
    <component
      :is="linkTag"
      class="in-dm-card__link"
      :class="{ 'in-dm-card__link--clickable': safeUrl }"
      v-bind="linkAttrs"
    >
      <div class="in-dm-card__media">
        <InDmImage :url="mediaUrl" />
      </div>
    </component>
    <!-- コンテンツ -->
    <div class="in-dm-card__content">
      <component
        :is="linkTag"
        class="in-dm-card__link in-dm-card__content-link"
        :class="{ 'in-dm-card__link--clickable': safeUrl, 'in-dm-card__content-link--clickable': safeUrl }"
        v-bind="linkAttrs"
      >
        <p class="in-dm-card__title">{{ title }}</p>
        <p v-if="subtitle" class="in-dm-card__subtitle">{{ subtitle }}</p>
      </component>
      <div v-if="cardButtons.length" class="in-dm-card__btns">
        <InDmCardButton v-for="(button, index) in cardButtons" :key="index" :name="button.name" :url="button.url" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import InDmCardButton, { InDmCardButtonProps } from '@/client/components/_atoms/in_dm_card_button'
import InDmImage from '@/client/components/_atoms/in_dm_image'
import { sanitizeHttpUrl } from '@/client/utils/string'

interface Props {
  title: string
  subtitle: string | null
  url: string | null
  mediaUrl: string
  cardButtons?: InDmCardButtonProps[]
}
const props = withDefaults(defineProps<Props>(), {
  cardButtons: () => []
})

const safeUrl = computed(() => sanitizeHttpUrl(props.url))
const linkTag = computed(() => (safeUrl.value ? 'a' : 'div'))
const linkAttrs = computed(() =>
  safeUrl.value ? { href: safeUrl.value, target: '_blank', rel: 'noopener noreferrer' } : {}
)
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/in-dm.scss' as *;
@use '@/client/assets/scss/variables.scss' as *;

.in-dm-card {
  width: 240px;
  overflow: hidden;
  background: $in-dm-chat-bg;
  border: 1px solid $in-dm-card-border;
  border-radius: 20px;
}

.in-dm-card__link {
  display: block;
  color: inherit;
  text-decoration: none;
}

.in-dm-card__link--clickable {
  cursor: pointer;
}

.in-dm-card__media {
  width: 100%;
  height: 172px;
  overflow: hidden;
}

.in-dm-card__content {
  position: relative;
  padding: $spacing-unit * 2;
  background: $in-dm-card-bg;
}

.in-dm-card__content-link--clickable::after {
  position: absolute;
  inset: 0;
  z-index: 0;
  content: '';
}

.in-dm-card__title {
  margin: 0;
  font-size: $font-large-size;
  font-weight: $font-weight-bold;
  line-height: 1.35;
  color: $in-dm-card-title;
}

.in-dm-card__subtitle {
  margin: 4px 0 0;
  font-size: $font-small-size;
  color: $in-dm-card-subtitle;
}

.in-dm-card__btns {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: $spacing-unit;
  margin-top: 12px;
}
</style>
